import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Fuel, Zap, Snowflake, Wrench, Car, Utensils, Warehouse, MessageSquareText,
  Star, Loader2, ChevronRight, Send, X, ShieldCheck, ArrowLeftRight, MapPin, Radio, ShoppingCart, Droplet,
} from "lucide-react";
import BackToCockpit from "@/components/BackToCockpit";

const SERVICE_ICONS = {
  fuel_jeta: Droplet,
  fuel_100ll: Fuel,
  gpu: Zap,
  ice: Snowflake,
  lav: Wrench,
  car_rental: Car,
  transport: Car,
  catering: Utensils,
  hangar: Warehouse,
};

const REQUEST_KINDS = [
  { key: "fuel", label: "Fuel", icon: Droplet, tone: "sky" },
  { key: "gpu", label: "GPU", icon: Zap, tone: "sky" },
  { key: "ice", label: "Ice", icon: Snowflake, tone: "sky" },
  { key: "lav", label: "Lav", icon: Wrench, tone: "slate" },
  { key: "transport", label: "Ground", icon: Car, tone: "sky" },
  { key: "catering", label: "Catering", icon: Utensils, tone: "slate" },
  { key: "hangar", label: "Hangar", icon: Warehouse, tone: "slate" },
  { key: "question", label: "Ask", icon: MessageSquareText, tone: "sky" },
];

// Star ratings row (0-5). Renders <span> when non-interactive to avoid nesting <button> inside <button>.
function StarRow({ value = 0, size = 12, interactive = false, onChange }) {
  const stars = [1, 2, 3, 4, 5];
  return (
    <div className="inline-flex items-center gap-0.5">
      {stars.map((s) => {
        const filled = s <= Math.round(value);
        const style = { width: size, height: size, color: filled ? "#4EDF8B" : "rgba(255,255,255,0.25)", fill: filled ? "#4EDF8B" : "transparent" };
        if (!interactive) {
          return (
            <span key={s} className="p-0.5 inline-flex" aria-hidden="true">
              <Star className="transition-colors" style={style} />
            </span>
          );
        }
        return (
          <button
            key={s}
            type="button"
            onClick={() => onChange && onChange(s)}
            className="cursor-pointer p-0.5"
            data-testid={`rate-star-${s}`}
          >
            <Star className="transition-colors" style={style} />
          </button>
        );
      })}
    </div>
  );
}

function AirportInput({ label, value, onChange, testId }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const to = useRef(null);
  useEffect(() => {
    if (!q || q.length < 1) { setResults([]); return; }
    clearTimeout(to.current);
    to.current = setTimeout(async () => {
      try {
        const r = await api.get("/airports/search", { params: { q, limit: 6 } });
        setResults(r.data.results || []);
      } catch { setResults([]); }
    }, 180);
    return () => clearTimeout(to.current);
  }, [q]);
  return (
    <div className="relative flex-1 min-w-0" data-testid={testId}>
      <div className="mono text-[10px] tracking-[0.24em] text-white/45 uppercase">{label}</div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white hover:text-sky transition-colors truncate"
        data-testid={`${testId}-picker`}
      >
        {value || "————"}
      </button>
      {open && (
        <div className="absolute left-0 right-0 mt-2 z-30 rounded-xl border border-sky/40 bg-obsidian shadow-2xl overflow-hidden max-w-[360px]">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value.toUpperCase())}
            placeholder="ICAO, city, name…"
            className="w-full bg-transparent px-3 py-3 outline-none text-white placeholder:text-white/40 border-b border-white/10"
            style={{ fontSize: 16 }}
            data-testid={`${testId}-search-input`}
          />
          <div className="max-h-64 overflow-y-auto">
            {results.map((a) => (
              <button
                key={a.icao}
                onMouseDown={(e) => { e.preventDefault(); onChange(a.icao); setOpen(false); setQ(""); }}
                className="w-full text-left px-3 py-2.5 hover:bg-sky/10 border-b border-white/[0.04] last:border-0"
                data-testid={`${testId}-option-${a.icao}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="mono text-sky text-sm">{a.icao}{a.iata ? ` · ${a.iata}` : ""}</div>
                  <div className="text-[10px] text-white/40">{a.country}</div>
                </div>
                <div className="text-sm text-white/90 truncate">{a.name}</div>
                <div className="text-[11px] text-white/50">{a.city}</div>
              </button>
            ))}
            {results.length === 0 && q && (
              <div className="px-3 py-3 text-white/40 text-sm">No airports found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FboCard({ fbo, onOpen, testId }) {
  const fp = fbo.fuel_prices || {};
  const svc = fbo.services || {};
  const activeServices = ["fuel_jeta", "gpu", "ice"].filter((k) => svc[k]);
  const savings = fp.jet_a_retail && fp.jet_a_pjp ? (fp.jet_a_retail - fp.jet_a_pjp).toFixed(2) : null;
  const isWinner = !!fbo.is_pjp_fuel_winner;
  return (
    <button
      type="button"
      onClick={() => onOpen(fbo)}
      data-testid={testId}
      className={`w-full text-left card !p-4 sm:!p-5 transition-colors ${isWinner ? "!border-avail/60" : "hover:border-sky/40"}`}
      style={isWinner ? { boxShadow: "0 0 22px rgba(46,213,115,0.22), inset 0 0 22px rgba(46,213,115,0.06)" } : {}}
    >
      {isWinner && (
        <div className="mb-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-avail/15 border border-avail/50" data-testid={`pjp-fuel-badge-${fbo.handle}`}>
          <div className="h-2 w-2 rounded-full bg-avail animate-pulse" style={{ boxShadow: "0 0 6px #2ED573" }} />
          <span className="mono text-[9px] font-bold tracking-[0.22em] text-avail">PJP FUEL · FIELD LOW</span>
        </div>
      )}
      <div className="flex items-start gap-3">
        {/* Logo placeholder */}
        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-white/10 to-white/[0.03] border border-white/15 grid place-items-center flex-shrink-0 mono text-[10px] font-bold text-white">
          {(fbo.brand || fbo.name).split(" ").map(s => s.charAt(0)).slice(0,2).join("")}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-display font-semibold text-white truncate">{fbo.name}</div>
              <div className="mono text-[11px] text-white/55 mt-0.5">{fbo.pjp_members_here || 0} PJP member{fbo.pjp_members_here === 1 ? "" : "s"} at this location</div>
            </div>
            <StarRow value={fbo.rating_avg} size={12} />
          </div>

          {/* Service icons */}
          <div className="flex items-center gap-2 mt-2">
            {activeServices.map((k) => {
              const Icon = SERVICE_ICONS[k] || ShoppingCart;
              return <Icon key={k} className="h-4 w-4 text-white/55" />;
            })}
            {!fbo.claimed && (
              <span className="ml-auto mono text-[9px] tracking-widest text-orange-hud/80 uppercase">UNCLAIMED</span>
            )}
          </div>
        </div>
      </div>

      {/* Fuel comparison */}
      {fp.jet_a_retail && (
        <div className="mt-3 pt-3 border-t border-white/[0.05] grid grid-cols-2 gap-2">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isWinner ? "bg-avail/[0.08] border border-avail/40" : "bg-sky/[0.06] border border-sky/25"}`}>
            <div className={`h-8 w-8 rounded-full bg-obsidian border grid place-items-center mono text-[9px] font-black ${isWinner ? "border-avail/60 text-avail" : "border-sky/40 text-sky"}`}>PJP</div>
            <div className="min-w-0">
              <div className={`mono text-[9px] tracking-widest ${isWinner ? "text-avail/80" : "text-sky/70"}`}>JET A</div>
              <div className="font-display font-semibold text-white text-base leading-none">${(fbo.pjp_fuel_winner_price ?? fp.jet_a_pjp)?.toFixed(2)}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10">
            <div className="h-8 w-8 rounded-full bg-white/[0.06] border border-white/15 grid place-items-center">
              <Droplet className="h-3.5 w-3.5 text-white/50" />
            </div>
            <div className="min-w-0">
              <div className="mono text-[9px] text-white/50 tracking-widest">RETAIL</div>
              <div className="font-display font-medium text-white/60 text-base leading-none line-through">${fp.jet_a_retail?.toFixed(2)}</div>
            </div>
          </div>
          {savings && parseFloat(savings) > 0 && (
            <div className="col-span-2 text-[10px] mono tracking-widest text-avail text-center pt-1">
              // SAVE ${savings}/GAL WITH PJP
            </div>
          )}
        </div>
      )}
    </button>
  );
}

/** FBO detail sheet — request buttons + thread + ratings + PIREPs */
function FboSheet({ fbo, onClose, onRatingChanged }) {
  const [tab, setTab] = useState("request");   // request | rate | pireps
  const [kinds, setKinds] = useState(new Set(["fuel"]));   // multi-select
  const [reqText, setReqText] = useState("");
  const [tail, setTail] = useState("");
  const [eta, setEta] = useState("");
  const [gallons, setGallons] = useState("");
  const [sending, setSending] = useState(false);
  const [myStars, setMyStars] = useState(0);
  const [review, setReview] = useState("");
  const [pireps, setPireps] = useState([]);
  const [newPirep, setNewPirep] = useState("");
  const [pirepCat, setPirepCat] = useState("general");

  const toggleKind = (k) => {
    setKinds((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const requiresTail = Array.from(kinds).some((k) => k !== "question");
  const tailMissing = requiresTail && !tail.trim();

  const submitRequest = async () => {
    if (kinds.size === 0) { toast.error("Select at least one service"); return; }
    if (tailMissing) {
      toast.error("A tail number is required for anything except 'Ask'");
      return;
    }
    setSending(true);
    try {
      // Fan out one request per selected kind
      const kindList = Array.from(kinds);
      for (const k of kindList) {
        const payload = {
          fbo_id: fbo.id,
          kind: k,
          text: reqText.trim(),
          tail_number: tail.trim() || null,
          eta: eta.trim() || null,
          fuel_gallons: k === "fuel" && gallons ? Number(gallons) : null,
        };
        await api.post("/unicom/request", payload);
      }
      const label = kindList.length === 1
        ? REQUEST_KINDS.find(r => r.key === kindList[0])?.label
        : `${kindList.length} services`;
      toast.success(
        fbo.claimed
          ? `${label} request sent to ${fbo.name}`
          : `${label} queued — ${fbo.name} hasn't claimed yet, PJP will forward it`
      );
      setReqText(""); setTail(""); setEta(""); setGallons("");
      setKinds(new Set(["fuel"]));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed");
    } finally { setSending(false); }
  };

  const submitRating = async () => {
    if (!myStars) return;
    try {
      const r = await api.post(`/unicom/fbos/${fbo.id}/rate`, { stars: myStars, review });
      toast.success("Rating submitted");
      onRatingChanged && onRatingChanged({ ...fbo, rating_avg: r.data.rating_avg, rating_count: r.data.rating_count });
    } catch (e) { toast.error(e?.response?.data?.detail || "Rating failed"); }
  };

  const submitPirep = async () => {
    if (!newPirep.trim()) return;
    try {
      const r = await api.post("/unicom/pireps", { icao: fbo.icao, text: newPirep, category: pirepCat });
      setPireps((prev) => [r.data, ...prev]);
      setNewPirep("");
      toast.success("PIREP posted");
    } catch (e) { toast.error(e?.response?.data?.detail || "PIREP failed"); }
  };

  useEffect(() => {
    if (tab === "pireps") {
      api.get(`/unicom/pireps/${fbo.icao}`).then((r) => setPireps(r.data.pireps || [])).catch(() => {});
    }
  }, [tab, fbo.icao]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center sm:p-4" data-testid="fbo-sheet">
      <div className="bg-obsidian border border-white/15 rounded-t-2xl sm:rounded-2xl max-w-lg w-full max-h-[92vh] overflow-y-auto"
           style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-obsidian/95 backdrop-blur border-b border-white/10 p-4 flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-sky/25 to-sky/[0.05] border border-sky/40 grid place-items-center flex-shrink-0 mono text-[10px] font-bold text-white">
            {(fbo.brand || fbo.name).split(" ").map(s => s.charAt(0)).slice(0,2).join("")}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-white truncate">{fbo.name}</div>
            <div className="mono text-[11px] text-white/50 flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {fbo.icao} · {fbo.city}
            </div>
          </div>
          <button onClick={onClose} className="p-2 -mr-2 text-white/60 hover:text-white" data-testid="fbo-sheet-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex gap-2 p-4 border-b border-white/[0.06]">
          <SheetTab active={tab === "request"} onClick={() => setTab("request")} testId="sheet-tab-request">Request</SheetTab>
          <SheetTab active={tab === "rate"} onClick={() => setTab("rate")} testId="sheet-tab-rate">Rate</SheetTab>
          <SheetTab active={tab === "pireps"} onClick={() => setTab("pireps")} testId="sheet-tab-pireps">PIREPs</SheetTab>
        </div>

        {tab === "request" && (
          <div className="p-4 space-y-4">
            <div>
              <div className="mono text-[10px] tracking-[0.24em] uppercase text-white/45 mb-2">// SELECT SERVICES (multi)</div>
              <div className="grid grid-cols-4 gap-2">
                {REQUEST_KINDS.map((r) => {
                  const Icon = r.icon;
                  const active = kinds.has(r.key);
                  const isAsk = r.key === "question";
                  const baseInactive = "bg-white/[0.03] border-white/10 text-white/70 hover:border-white/20";
                  const baseActive = "bg-sky/15 border-sky text-white shadow-[0_0_14px_rgba(75,156,255,0.28)]";
                  const askInactive = "bg-avail/[0.08] border-avail/40 text-avail hover:bg-avail/[0.14]";
                  const askActive = "bg-avail/25 border-avail text-white shadow-[0_0_16px_rgba(46,213,115,0.35)]";
                  const cls = isAsk ? (active ? askActive : askInactive) : (active ? baseActive : baseInactive);
                  return (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => toggleKind(r.key)}
                      data-testid={`req-kind-${r.key}`}
                      aria-pressed={active}
                      className={`relative flex flex-col items-center gap-1 p-2.5 rounded-xl border transition-all min-h-[64px] ${cls}`}
                    >
                      {active && (
                        <span
                          className="absolute top-1.5 right-1.5 h-4 w-4 rounded-full grid place-items-center"
                          style={{ background: isAsk ? "#2ED573" : "#4B9CFF", color: "#0A0F1A" }}
                          data-testid={`req-kind-${r.key}-check`}
                        >
                          <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </span>
                      )}
                      <Icon className="h-4 w-4" />
                      <span className="text-[11px] mono tracking-wide">{r.label}</span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 mono text-[10px] tracking-wider text-white/45">
                Tap to toggle · <span className="text-avail">Ask is free & always available</span> · {kinds.size} selected
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="eyebrow-muted mb-1 flex items-center gap-1">
                  Tail # {requiresTail && <span className="text-[#EB5757]" title="Required for anything except Ask">*</span>}
                </div>
                <input
                  value={tail}
                  onChange={(e) => setTail(e.target.value.toUpperCase())}
                  placeholder="N550AE"
                  className={`input !py-2 ${tailMissing ? "!border-[#EB5757]/60" : ""}`}
                  style={{ fontSize: 16 }}
                  data-testid="req-tail"
                  aria-invalid={tailMissing}
                />
                {tailMissing && (
                  <div className="mt-1 text-[11px] text-[#FF7A7A] mono tracking-wide" data-testid="req-tail-warning">
                    Tail required for {Array.from(kinds).filter(k => k !== "question").map(k => REQUEST_KINDS.find(r => r.key === k)?.label).join(", ")}
                  </div>
                )}
              </div>
              <div>
                <div className="eyebrow-muted mb-1">ETA</div>
                <input value={eta} onChange={(e) => setEta(e.target.value)} placeholder="1430L" className="input !py-2" style={{ fontSize: 16 }} data-testid="req-eta" />
              </div>
            </div>

            {kinds.has("fuel") && (
              <div>
                <div className="eyebrow-muted mb-1">Requested Gallons (Jet A)</div>
                <input type="number" min="0" value={gallons} onChange={(e) => setGallons(e.target.value)} placeholder="e.g. 800" className="input !py-2" style={{ fontSize: 16 }} data-testid="req-gallons" />
              </div>
            )}

            <div>
              <div className="eyebrow-muted mb-1">Message</div>
              <textarea rows={3} value={reqText} onChange={(e) => setReqText(e.target.value)} placeholder="e.g. Land in an hour, need ice + fuel to tabs. See you soon."
                        className="input" style={{ fontSize: 16 }} data-testid="req-text" />
            </div>

            {!fbo.claimed && (
              <div className="text-[11px] text-orange-hud/90 mono tracking-wide bg-orange-hud/10 border border-orange-hud/30 rounded-lg px-3 py-2" data-testid="req-unclaimed-notice">
                {fbo.name} hasn't claimed their PJP account yet. Your request will be stored + PJP will follow up with them via {fbo.public_email || "their published contact"}.
              </div>
            )}

            <button
              onClick={submitRequest}
              disabled={sending || kinds.size === 0 || tailMissing}
              data-testid="req-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-sm tracking-tight disabled:opacity-40 min-h-[48px]"
              style={{ background: "#4B9CFF", color: "#0A0F1A", boxShadow: "0 0 20px rgba(75,156,255,0.35)" }}
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? "Sending…" : `Send ${kinds.size > 1 ? `${kinds.size} requests` : "via UNICOM"}`}
            </button>
          </div>
        )}

        {tab === "rate" && (
          <div className="p-4 space-y-4">
            <div>
              <div className="mono text-[10px] tracking-[0.24em] uppercase text-white/45 mb-2">// YOUR RATING</div>
              <StarRow value={myStars} size={30} interactive onChange={setMyStars} />
            </div>
            <div>
              <div className="eyebrow-muted mb-1">Review (optional)</div>
              <textarea rows={3} value={review} onChange={(e) => setReview(e.target.value)} placeholder="Fast turn, great customer service, hot cookies…"
                        className="input" style={{ fontSize: 16 }} data-testid="rate-review" />
            </div>
            <button
              onClick={submitRating}
              disabled={!myStars}
              data-testid="rate-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-sm tracking-tight disabled:opacity-40 min-h-[48px]"
              style={{ background: "#4B9CFF", color: "#0A0F1A" }}
            >
              Submit Rating
            </button>
            <div className="text-[11px] text-white/40 mono tracking-wide text-center">
              Current: {fbo.rating_avg?.toFixed(1) || "0.0"} ★ ({fbo.rating_count || 0} reviews)
            </div>
          </div>
        )}

        {tab === "pireps" && (
          <div className="p-4 space-y-4">
            <div>
              <div className="eyebrow-muted mb-1">Field report / PIREP</div>
              <textarea rows={2} value={newPirep} onChange={(e) => setNewPirep(e.target.value)} placeholder="Ramp closed after 22Z, customs slow, taxi Y1 rough…"
                        className="input" style={{ fontSize: 16 }} data-testid="pirep-text" />
              <div className="flex flex-wrap gap-1 mt-2">
                {["general", "ramp", "fuel", "customs", "ground_transport", "food", "weather"].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setPirepCat(c)}
                    data-testid={`pirep-cat-${c}`}
                    className={`mono text-[10px] tracking-widest px-2.5 py-1 rounded-full border ${
                      pirepCat === c ? "bg-sky/20 border-sky text-white" : "bg-white/[0.03] border-white/10 text-white/60"
                    }`}
                  >
                    {c.toUpperCase()}
                  </button>
                ))}
              </div>
              <button
                onClick={submitPirep}
                disabled={!newPirep.trim()}
                data-testid="pirep-submit"
                className="mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-semibold text-sm disabled:opacity-40 min-h-[44px]"
                style={{ background: "#4B9CFF", color: "#0A0F1A" }}
              >
                Post PIREP
              </button>
            </div>
            <div className="space-y-2">
              {pireps.length === 0 && <div className="text-white/40 text-sm text-center py-4">No PIREPs at {fbo.icao} yet.</div>}
              {pireps.map((p) => (
                <div key={p.id} className="card !p-3" data-testid={`pirep-${p.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="mono text-[10px] px-1.5 py-0.5 rounded border border-white/15 text-white/60 tracking-wider">{p.category.toUpperCase()}</span>
                    <span className="text-xs text-white/80 truncate">{p.author_name}</span>
                    {p.faa_verified && <ShieldCheck className="h-3 w-3 text-avail" />}
                    <span className="ml-auto mono text-[10px] text-white/40">{new Date(p.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="text-sm text-white/85 whitespace-pre-wrap">{p.text}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SheetTab({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold min-h-[36px] transition-colors ${
        active ? "bg-sky/20 border border-sky/50 text-white" : "bg-white/[0.04] border border-white/10 text-white/70 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

export default function Unicom() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [fromIcao, setFromIcao] = useState(params.get("from") || user?.home_base_icao || "KPBI");
  const [toIcao, setToIcao] = useState(params.get("to") || "KTEB");
  const [tab, setTab] = useState("departure");
  const [depData, setDepData] = useState(null);
  const [arrData, setArrData] = useState(null);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [openFbo, setOpenFbo] = useState(null);

  useEffect(() => {
    if (!loading && !user) nav("/login?next=/unicom");
  }, [user, loading, nav]);

  const loadRoute = async () => {
    if (!fromIcao || !toIcao) return;
    setLoadingRoute(true);
    try {
      const r = await api.get("/unicom/route", { params: { from_icao: fromIcao, to_icao: toIcao } });
      setDepData(r.data.departure);
      setArrData(r.data.arrival);
      setParams({ from: fromIcao, to: toIcao });
    } catch (e) {
      toast.error("Failed to load FBOs");
    } finally {
      setLoadingRoute(false);
    }
  };

  useEffect(() => { if (user) loadRoute(); /* eslint-disable-next-line */ }, [user?.id, fromIcao, toIcao]);

  const swap = () => {
    setFromIcao(toIcao);
    setToIcao(fromIcao);
  };

  if (loading) return <div className="max-w-4xl mx-auto px-6 py-12 text-white/50">Loading…</div>;
  if (!user) return null;

  const activeData = tab === "departure" ? depData : arrData;
  const fbos = activeData?.fbos || [];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <BackToCockpit />
      {/* Header */}
      <div className="mb-6 flex items-center gap-2">
        <Radio className="h-4 w-4 text-sky" />
        <div className="mono text-[10px] tracking-[0.24em] text-sky/80 uppercase">// PJP · UNICOM</div>
      </div>
      <h1
        className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl mb-1"
        style={{ background: "linear-gradient(90deg,#4B9CFF,#7EC8FF,#A8DBFF)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", textShadow: "0 0 40px rgba(75,156,255,0.35)" }}
      >
        UNICOM
      </h1>
      <div className="text-white/55 text-sm mb-6">
        Text-message any FBO on your route — fuel, GPU, ice, ground transport, or just ask a question.
      </div>

      {/* Route picker */}
      <div className="card !p-4 sm:!p-6 mb-5">
        <div className="flex items-center gap-3">
          <AirportInput label="FROM" value={fromIcao} onChange={setFromIcao} testId="unicom-from" />
          <button onClick={swap} className="p-3 rounded-full hover:bg-white/[0.04] text-sky flex-shrink-0" title="Swap" data-testid="unicom-swap">
            <ArrowLeftRight className="h-5 w-5" />
          </button>
          <AirportInput label="TO" value={toIcao} onChange={setToIcao} testId="unicom-to" />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <UnicomTab active={tab === "departure"} onClick={() => setTab("departure")} testId="unicom-tab-departure">
          Departure · {fromIcao}
        </UnicomTab>
        <UnicomTab active={tab === "arrival"} onClick={() => setTab("arrival")} testId="unicom-tab-arrival">
          Arrival · {toIcao}
        </UnicomTab>
      </div>

      {loadingRoute ? (
        <div className="card !p-10 text-center">
          <Loader2 className="h-6 w-6 mx-auto animate-spin text-sky" />
          <div className="mt-2 text-sm text-white/50">Loading FBOs at {tab === "departure" ? fromIcao : toIcao}…</div>
        </div>
      ) : fbos.length === 0 ? (
        <div className="card !p-10 text-center" data-testid="unicom-empty">
          <div className="font-display text-lg text-white mb-1">No FBOs on file at {tab === "departure" ? fromIcao : toIcao}</div>
          <div className="text-white/50 text-sm">Ping PJP with the airport code and we'll add them — takes ~24h.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {fbos.map((f) => (
            <FboCard key={f.id} fbo={f} onOpen={setOpenFbo} testId={`fbo-card-${f.handle}`} />
          ))}
        </div>
      )}

      {openFbo && (
        <FboSheet
          fbo={openFbo}
          onClose={() => setOpenFbo(null)}
          onRatingChanged={(next) => {
            setOpenFbo(next);
            const upd = (data) => data ? { ...data, fbos: data.fbos.map(f => f.id === next.id ? next : f) } : data;
            setDepData(upd); setArrData(upd);
          }}
        />
      )}
    </div>
  );
}

function UnicomTab({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full text-sm font-semibold transition-all min-h-[44px] ${
        active
          ? "bg-sky/20 border border-sky/50 text-white shadow-[0_0_14px_rgba(75,156,255,0.25)]"
          : "bg-white/[0.03] border border-white/10 text-white/70 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}
